import type { Root, RootContent, Element, ElementContent } from "hast";

/**
 * Recursively concatenate all text node values from a HAST subtree.
 * Handles inline formatting (e.g., `## Key **Insights**` → "Key Insights").
 */
function nodeToText(node: RootContent | ElementContent): string {
  if (node.type === "text") return node.value;
  if ("children" in node) {
    return node.children.map(nodeToText).join("");
  }
  return "";
}

function isHeadingElement(
  node: RootContent,
): node is Element & { tagName: "h1" | "h2" | "h3" } {
  return (
    node.type === "element" &&
    (node.tagName === "h1" || node.tagName === "h2" || node.tagName === "h3")
  );
}

function makeSectionElement(
  kind: "section" | "preamble",
  heading: string | undefined,
  children: RootContent[],
): Element {
  const properties: Record<string, string | boolean> = {
    dataReportSection: "true",
    dataSectionKind: kind,
  };
  if (kind === "section" && heading !== undefined) {
    properties.dataSectionHeading = heading;
  }
  return {
    type: "element",
    tagName: "section",
    properties,
    children: children as ElementContent[],
  };
}

/**
 * Rehype plugin that wraps content between h1/h2/h3 headings into `<section>`
 * elements with data attributes for the report comment mode click-to-comment UX.
 *
 * - Content before the first h1/h2/h3 → `<section data-report-section data-section-kind="preamble">`
 * - Content from an h1/h2/h3 through the next h1/h2/h3 (or end) → `<section data-report-section data-section-kind="section" data-section-heading="...">`
 * - If no h1/h2/h3 exists at all, the entire body is wrapped as a single preamble section.
 *
 * Must run **after** rehypeRaw so custom HTML elements are already parsed into HAST nodes.
 */
export function rehypeSectionWrap() {
  return (tree: Root) => {
    const children = tree.children;

    // Check if there are any h1/h2/h3 headings
    const hasHeadings = children.some(
      (node) => node.type === "element" && isHeadingElement(node),
    );

    // No headings at all → wrap everything in a single preamble section
    if (!hasHeadings) {
      if (children.length > 0) {
        tree.children = [makeSectionElement("preamble", undefined, children)];
      }
      return;
    }

    const sections: RootContent[] = [];
    let currentChildren: RootContent[] = [];
    let currentHeading: string | undefined;
    let inSection = false;

    for (const node of children) {
      if (isHeadingElement(node)) {
        // Flush previous group
        if (currentChildren.length > 0) {
          if (inSection) {
            sections.push(
              makeSectionElement("section", currentHeading, currentChildren),
            );
          } else {
            sections.push(
              makeSectionElement("preamble", undefined, currentChildren),
            );
          }
        }
        // Start a new headed section
        currentHeading = nodeToText(node);
        currentChildren = [node];
        inSection = true;
      } else {
        currentChildren.push(node);
      }
    }

    // Flush the last group
    if (currentChildren.length > 0) {
      if (inSection) {
        sections.push(
          makeSectionElement("section", currentHeading, currentChildren),
        );
      } else {
        sections.push(
          makeSectionElement("preamble", undefined, currentChildren),
        );
      }
    }

    tree.children = sections;
  };
}
